import type { RefObject } from 'react'

import { useEffect, useState } from 'react'

interface InViewOptions {
    once?: boolean
    rootMargin?: string
    threshold?: number
}

// True once the element has scrolled into view. Starts false on the server and the first client render.
export const useInView = (
    ref: RefObject<Element | null>,
    { once = true, rootMargin = '0px 0px -12% 0px', threshold = 0 }: InViewOptions = {}
): boolean => {
    const [inView, setInView] = useState(false)

    useEffect(() => {
        const element = ref.current

        if (!element) return

        if (typeof IntersectionObserver === 'undefined') {
            setInView(true)
            return
        }

        const observer = new IntersectionObserver(entries => {
            const entry = entries[0]

            if (!entry) return

            if (entry.isIntersecting) {
                setInView(true)

                if (once) observer.disconnect()
            }
            else if (!once) {
                setInView(false)
            }
        }, { rootMargin, threshold })

        observer.observe(element)

        return () => observer.disconnect()
    }, [ref, once, rootMargin, threshold])

    return inView
}
